import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import {
  ERROR_FIELD_CLASS,
  IBlockItemFormProps
} from 'views/Pages/PagesForm/constants';
import {
  pageFormSchema,
  videoItemSchema
} from 'views/Pages/PagesForm/validation-schema';
import { z } from 'zod';

import Checkbox from 'components/Checkbox/Checkbox';
import { cn } from 'components/lib/utils';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../../components/ui/form';
import { Input } from '../../../../../../../components/ui/input';
import { BlockSegmentsField } from '../../Fields/BlockSegmentsField';
import { BlockVisibilityField } from '../../Fields/BlockVisibilityField';

type VideoItemBlockType = z.infer<typeof videoItemSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type VideoItemFieldProps<T extends keyof VideoItemBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: VideoItemBlockType[T];
  };

export const VideoItemForm: React.FC<IBlockItemFormProps> = ({
  item,
  blockIndex,
  itemIndex
}) => {
  const { control, getFieldState, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}.items.${itemIndex}` as const;

  const getFieldError = (fieldName: string) => {
    const fieldState = getFieldState(fieldName as any);
    return fieldState.error;
  };

  return (
    <>
      <FormField
        control={control}
        name={`${basePath}.url`}
        render={({
          field: { onChange, value }
        }: {
          field: VideoItemFieldProps<'url'>;
        }) => (
          <FormItem>
            <FormLabel>* Video URL</FormLabel>
            <FormControl>
              <Input
                title="Video URL"
                placeholder="Add a video URL to your page"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const url = e.target.value;
                  onChange(url);
                  trigger('blocksV2');
                }}
                value={value ?? undefined}
                className={cn(
                  'w-full',
                  getFieldError(`${basePath}.url`)?.message
                    ? ERROR_FIELD_CLASS
                    : ''
                )}
                required
                data-testid="PagesForm-Upload-Video"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.mobileUrl`}
        render={({
          field: { onChange, value }
        }: {
          field: VideoItemFieldProps<'mobileUrl'>;
        }) => (
          <FormItem>
            <FormLabel>Mobile Video URL</FormLabel>
            <FormControl>
              <Input
                title="Mobile Video URL"
                placeholder="Add a mobile video URL to your page"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const url = e.target.value;
                  onChange(url);
                  trigger('blocksV2');
                }}
                value={value ?? undefined}
                className={cn(
                  'w-full',
                  getFieldError(`${basePath}.mobileUrl`)?.message
                    ? ERROR_FIELD_CLASS
                    : ''
                )}
                data-testid="PagesForm-Upload-MobileVideo"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.autoplay`}
        render={({ field }: { field: VideoItemFieldProps<'autoplay'> }) => (
          <FormItem>
            <FormControl>
              <Checkbox
                checked={field.value ?? false}
                onCheckedChange={field.onChange}
                label={'Autoplay'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <BlockVisibilityField basePath={basePath} />
      <BlockSegmentsField basePath={basePath} />
    </>
  );
};
